import type { FichaCliente } from './BuscarCliente';

/**
 * Buscar personas por parecido, no solo por coincidencia exacta.
 *
 * Con el directorio entero en memoria esto sale gratis: son 2.455 fichas y
 * filtrarlas es cosa de milisegundos, sin una sola consulta a la base. Por
 * eso puede permitirse aguantar erratas ("gonzales" encuentra a "González"),
 * el orden cambiado ("pérez juan" encuentra a "Juan Pérez") y las tildes que
 * nadie escribe con prisa.
 *
 * Las coincidencias buenas siempre van delante: primero la palabra exacta,
 * después la que empieza igual, después la que la contiene, y solo al final
 * la que se parece. Que aparezca un parecido nunca debe enterrar al que se
 * escribió bien.
 */

const PUNTOS_EXACTA = 100;
const PUNTOS_PREFIJO = 80;
const PUNTOS_DENTRO = 55;
const PUNTOS_NUMERO = 90;
/** Un parecido nunca puede llegar a donde llega una coincidencia real. */
const PUNTOS_PARECIDA = 40;

/** Debajo de esto una errata es indistinguible de otra palabra. */
const MINIMO_PARA_PARECERSE = 4;

export const normalizar = (texto: string): string =>
  texto
    .normalize('NFD')
    // eslint-disable-next-line no-misleading-character-class
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const soloDigitos = (texto: string): string => texto.replace(/\D/g, '');

/**
 * Distancia de edición, con freno.
 *
 * En cuanto se ve que la diferencia pasa del tope se abandona: comparar a
 * fondo dos palabras que no se parecen en nada es tiempo tirado, y esto se
 * ejecuta miles de veces por cada letra que se teclea.
 */
const distancia = (a: string, b: string, tope: number): number => {
  if (Math.abs(a.length - b.length) > tope) return tope + 1;

  let previa = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const fila = [i];
    let mejorDeLaFila = i;
    for (let j = 1; j <= b.length; j++) {
      const coste = a[i - 1] === b[j - 1] ? 0 : 1;
      const valor = Math.min(
        previa[j] + 1,
        fila[j - 1] + 1,
        previa[j - 1] + coste
      );
      fila.push(valor);
      if (valor < mejorDeLaFila) mejorDeLaFila = valor;
    }
    if (mejorDeLaFila > tope) return tope + 1;
    previa = fila;
  }
  return previa[b.length];
};

/** Cuántas erratas se le perdonan a una palabra según lo larga que sea. */
const topeDeErratas = (palabra: string): number =>
  palabra.length >= 7 ? 2 : palabra.length >= MINIMO_PARA_PARECERSE ? 1 : 0;

type Indice = { palabras: string[]; digitos: string };

/**
 * El texto de cada ficha, ya preparado para comparar.
 *
 * Se guarda en un `WeakMap` para no rehacerlo con cada tecla; cuando el
 * directorio se renueva, las fichas viejas se van solas de memoria.
 */
const indices = new WeakMap<FichaCliente, Indice>();

const indiceDe = (ficha: FichaCliente): Indice => {
  const guardado = indices.get(ficha);
  if (guardado) return guardado;

  const campos = [
    ficha.nombre,
    ficha.empresa,
    ficha.correo,
    ficha.documento,
    ficha.telefono,
  ].filter(Boolean) as string[];

  const indice: Indice = {
    palabras: normalizar(campos.join(' '))
      .split(/[^a-z0-9@.]+/)
      .filter((p) => p.length > 0),
    digitos: soloDigitos(`${ficha.documento ?? ''} ${ficha.telefono ?? ''}`),
  };
  indices.set(ficha, indice);
  return indice;
};

/** Lo mejor que consigue una palabra buscada contra una ficha. */
const puntuarPalabra = (
  indice: Indice,
  buscada: string,
  esNumero: boolean
): number => {
  if (esNumero && buscada.length >= 3 && indice.digitos.includes(buscada)) {
    return PUNTOS_NUMERO;
  }

  let mejor = 0;
  const tope = topeDeErratas(buscada);

  for (const palabra of indice.palabras) {
    if (palabra === buscada) return PUNTOS_EXACTA;
    if (palabra.startsWith(buscada)) {
      mejor = Math.max(mejor, PUNTOS_PREFIJO);
      continue;
    }
    if (palabra.includes(buscada)) {
      mejor = Math.max(mejor, PUNTOS_DENTRO);
      continue;
    }
    // Solo se mira el parecido si no hubo nada mejor: es lo caro.
    if (tope > 0 && mejor < PUNTOS_PARECIDA) {
      const d = distancia(palabra, buscada, tope);
      if (d <= tope) mejor = Math.max(mejor, PUNTOS_PARECIDA - (d - 1) * 8);
    }
  }
  return mejor;
};

const UN_ANO = 365 * 24 * 60 * 60 * 1000;

/**
 * Desempate: entre dos coincidencias igual de buenas, delante quien vino hace
 * poco y quien viene a menudo. Es casi siempre a quien se está buscando.
 */
const puntosDeCostumbre = (ficha: FichaCliente): number => {
  const visitas = Math.min(ficha.cuantas ?? 1, 10);
  const cuando = ficha.ultima?.getTime();
  if (!cuando) return visitas;
  const antiguedad = Date.now() - cuando;
  const frescura = antiguedad < UN_ANO ? 15 * (1 - antiguedad / UN_ANO) : 0;
  return visitas + frescura;
};

export type Resultado = {
  ficha: FichaCliente;
  /** Para ordenar: incluye el bono de "cliente habitual". */
  puntos: number;
  /**
   * Solo lo que puntuó la coincidencia en sí.
   *
   * Va aparte porque el bono de habitualidad podía empujar una simple errata
   * por encima del listón y hacer pasar por exacto lo que no lo era.
   */
  coincidencia: number;
};

/**
 * Busca `consulta` entre las fichas y devuelve las mejores.
 *
 * Todas las palabras de la búsqueda tienen que encontrar algo: quien escribe
 * "juan noi" quiere a Juan de NOI, no a todos los Juanes y encima toda la
 * gente de NOI.
 */
export const buscarParecidos = (
  fichas: FichaCliente[],
  consulta: string,
  limite: number
): Resultado[] => {
  const palabras = normalizar(consulta)
    .split(/\s+/)
    .filter((p) => p.length > 0);

  if (palabras.length === 0) {
    return [...fichas]
      .sort((a, b) => (b.ultima?.getTime() ?? 0) - (a.ultima?.getTime() ?? 0))
      .slice(0, limite)
      .map((ficha) => ({ ficha, puntos: 0, coincidencia: 0 }));
  }

  const numericas = palabras.map((p) => /^\d+$/.test(p));
  const encontradas: Resultado[] = [];

  for (const ficha of fichas) {
    const indice = indiceDe(ficha);
    let total = 0;
    let sirve = true;

    for (let i = 0; i < palabras.length; i++) {
      const puntos = puntuarPalabra(indice, palabras[i], numericas[i]);
      if (puntos === 0) {
        sirve = false;
        break;
      }
      total += puntos;
    }

    if (sirve) {
      encontradas.push({
        ficha,
        puntos: total + puntosDeCostumbre(ficha),
        coincidencia: total,
      });
    }
  }

  return encontradas.sort((a, b) => b.puntos - a.puntos).slice(0, limite);
};

/** Si lo mejor que hay es un parecido, conviene decirlo en pantalla. */
export const soloHayParecidos = (
  resultados: Resultado[],
  consulta: string
): boolean => {
  const palabras = normalizar(consulta).split(/\s+/).filter(Boolean);
  if (!palabras.length || !resultados.length) return false;
  return resultados[0].coincidencia < PUNTOS_DENTRO * palabras.length;
};
